import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, Eye, Pencil, PenLine, Search, Sparkles, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, get, imgUrl, errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader, StatusBadge, ORIGIN, ConfirmDelete } from "@/admin/ui";
import { GenerateDialog } from "@/admin/GenerateDialog";
import { useAdminCategories, useRefresh } from "@/admin/hooks";

const TABS = [["all", "Todas"], ["published", "Publicadas"], ["scheduled", "Programadas"], ["draft", "Borradores"]];
const iconBtn = "h-8 w-8 grid place-items-center border border-transparent hover:border-border hover:bg-background transition-colors";

const Row = ({ a, onFeature, onDelete }) => (
  <div className="grid grid-cols-[64px_1fr] md:grid-cols-[72px_1fr_auto_auto] items-center gap-4 px-4 py-3 border-b border-border last:border-0 hover:bg-muted/40" data-testid={`article-row-${a.id}`}>
    {a.image_url ? <img src={imgUrl(a.image_url)} alt="" className="h-12 w-16 md:w-[72px] object-cover" /> : <div className="h-12 w-16 md:w-[72px] cover-fallback" />}
    <div className="min-w-0">
      <Link to={`/admin/noticias/${a.id}`} className="font-medium leading-snug hover:text-primary line-clamp-2" data-testid={`article-row-title-${a.id}`}>{a.title}</Link>
      <p className="text-xs text-muted-foreground mt-1">{a.category_name} · {ORIGIN[a.origin] || "Manual"} · {timeAgo(a.status === "scheduled" ? a.publish_at : a.updated_at)}</p>
    </div>
    <div className="hidden md:flex items-center gap-3">
      {a.featured && <span className="kicker text-[10px] text-gold flex items-center gap-1"><Star className="h-3 w-3 fill-current" />Portada</span>}
      <span className="text-xs text-muted-foreground flex items-center gap-1 w-14"><Eye className="h-3 w-3" />{a.views || 0}</span>
      <StatusBadge status={a.status} />
    </div>
    <div className="col-span-2 md:col-span-1 flex items-center justify-end gap-1">
      <Link to={`/admin/noticias/${a.id}`} className={iconBtn} aria-label="Editar" data-testid={`article-edit-${a.id}`}><Pencil className="h-4 w-4" /></Link>
      <button className={iconBtn} onClick={() => onFeature(a)} aria-label="Destacar en portada" data-testid={`article-feature-${a.id}`}><Star className={`h-4 w-4 ${a.featured ? "fill-gold text-gold" : ""}`} /></button>
      {a.status === "published" && <a href={`/noticia/${a.slug}`} target="_blank" rel="noopener noreferrer" className={iconBtn} aria-label="Ver en la web" data-testid={`article-view-${a.id}`}><ExternalLink className="h-4 w-4" /></a>}
      <ConfirmDelete title="¿Eliminar esta noticia?" description="Se borrará de la web de forma definitiva." onConfirm={() => onDelete(a)}>
        <button className={`${iconBtn} hover:text-destructive`} aria-label="Eliminar" data-testid={`article-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
      </ConfirmDelete>
    </div>
  </div>
);

export default function Articles() {
  const navigate = useNavigate();
  const refresh = useRefresh();
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data: cats } = useAdminCategories();
  const params = { status: status === "all" ? undefined : status, category: category === "all" ? undefined : category, q: q || undefined, page };
  const { data, isLoading } = useQuery({ queryKey: ["admin-articles", params], queryFn: () => get("/admin/articles", params), placeholderData: keepPreviousData });

  const feature = useMutation({
    mutationFn: (a) => api.patch(`/admin/articles/${a.id}`, { featured: !a.featured }),
    onSuccess: (_, a) => { toast.success(a.featured ? "Quitada de portada" : "Destacada en portada"); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: (a) => api.delete(`/admin/articles/${a.id}`),
    onSuccess: () => { toast.success("Noticia eliminada"); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="admin-articles-page">
      <PageHeader kicker="Contenido" title="Noticias" description="Todo lo publicado, programado y en borrador. Pulsa una noticia para editarla.">
        <GenerateDialog trigger={<Button variant="outline" className="border-foreground" data-testid="articles-generate-btn"><Sparkles /> Generar con IA</Button>} />
        <Button onClick={() => navigate("/admin/noticias/nueva")} data-testid="articles-new-btn"><PenLine /> Nueva noticia</Button>
      </PageHeader>
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between mb-4">
        <div className="flex flex-wrap gap-1">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => { setStatus(k); setPage(1); }} data-testid={`articles-tab-${k}`}
              className={`px-3.5 py-2 text-sm border transition-colors ${status === k ? "bg-foreground text-background border-foreground" : "border-border bg-card hover:border-foreground/40"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar por título" className="pl-9 w-full sm:w-56 bg-card" data-testid="articles-search-input" />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-44 bg-card" data-testid="articles-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las secciones</SelectItem>
              {cats?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-card border border-border" data-testid="articles-list">
        {isLoading && <p className="p-6 text-sm text-muted-foreground">Cargando…</p>}
        {data?.items.map((a) => <Row key={a.id} a={a} onFeature={feature.mutate} onDelete={remove.mutate} />)}
        {data && !data.items.length && <p className="p-10 text-center text-muted-foreground" data-testid="articles-empty">No hay noticias con estos filtros.</p>}
      </div>
      {data && data.pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">{data.total} noticias · página {data.page} de {data.pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="articles-prev-page">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="articles-next-page">Siguiente</Button>
          </div>
        </div>
      )}
    </div>
  );
}
